import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import { LogOut, User, Settings, ChevronDown, ChevronUp } from './Icons';
import toast from 'react-hot-toast';
import user_icon from '../assets/user_icon.svg';
import notificationIcon from '../assets/whatsapp.png';
import { env } from '@/common/config';
import { NotificationDrawer } from './NotificationDrawer';

import { logoutUser } from '@/modules/Authentication';
import { usePermissions } from '@/common/hooks/usePermissions';
import { useDocumentTitle } from '@/common/hooks/useDocumentTitle';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import { PERMISSIONS } from '@/common/config/permissions';
import { useNotificationRouter } from '@/common/hooks/useNotificationRouter';
import { useNotificationStream } from '@/common/hooks/useNotificationStream.jsx';
import { useOneSignal } from '@/common/hooks/useOneSignal';
import { clearConversationDrafts } from '../whatsapp-chat/utils/conversationDrafts';
import { getActivePageTitle } from './Breadcrumbs';
import { LogoutConfirmModal } from './LogoutConfirmModal';
import {
  getNotifications,
  markNotificationAsRead,
  getUserProfile,
} from '../api';

const CONTACT_NAME_STORAGE_KEY = 'crm-contact-overview-name';

export default function Header({ toggleSidebar, sidebarOpen = false }) {
  const navigate = useNavigate();
  const { handleNotificationNavigation } = useNotificationRouter();
  const location = useLocation();
  const { logout: oneSignalLogout } = useOneSignal();
  const { pathname, state, search } = location;
  const [overviewContactName, setOverviewContactName] = useState(() => {
    try {
      return sessionStorage.getItem(CONTACT_NAME_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const [quotationCustomerName, setQuotationCustomerName] = useState(() => {
    try {
      return sessionStorage.getItem('quotation-customer-name') || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (pathname !== '/crm-contacts/overview') {
      setOverviewContactName('');
      return;
    }
    const syncName = () => {
      try {
        setOverviewContactName(
          sessionStorage.getItem(CONTACT_NAME_STORAGE_KEY) || '',
        );
      } catch {
        setOverviewContactName('');
      }
    };
    syncName();
    window.addEventListener('crm-contact-overview-updated', syncName);
    return () =>
      window.removeEventListener('crm-contact-overview-updated', syncName);
  }, [pathname]);

  useEffect(() => {
    if (!pathname.startsWith('/quotations/customer')) {
      setQuotationCustomerName('');
      return;
    }
    const syncName = () => {
      try {
        setQuotationCustomerName(
          sessionStorage.getItem('quotation-customer-name') || '',
        );
      } catch {
        setQuotationCustomerName('');
      }
    };
    syncName();
    window.addEventListener('quotation-customer-name-updated', syncName);
    return () =>
      window.removeEventListener('quotation-customer-name-updated', syncName);
  }, [pathname]);

  const pageTitle =
    pathname === '/whatsapp-chat' || pathname === '/email-inbox'
      ? 'Chats'
      : pathname === '/crm-contacts/overview' && overviewContactName
        ? overviewContactName
        : pathname.startsWith('/quotations/customer') && quotationCustomerName
          ? quotationCustomerName
          : getActivePageTitle(pathname, state, search);
  useDocumentTitle(
    pathname === '/not-found'
      ? state?.unavailable === 'card'
        ? 'Business card unavailable | Aexon'
        : 'Page Not Found | Aexon'
      : pageTitle,
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);

  useMobileScrollLock(dropdownOpen);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationRef = useRef(null);

  useOutsideClick(dropdownRef, () => setDropdownOpen(false), dropdownOpen);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  const { can } = usePermissions();
  const canViewProfile = can(PERMISSIONS.PROFILE.VIEW);

  const { data: notificationsData, refetch: refetchNotifications } =
    useInfiniteQuery({
      queryKey: ['notifications', companyId],
      queryFn: ({ pageParam = 1 }) =>
        getNotifications(companyId, { page: pageParam }),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.next) {
          return allPages.length + 1;
        }
        return undefined;
      },
      enabled: !!companyId,
    });

  const notificationsList =
    notificationsData?.pages?.flatMap((page) => page.results) || [];

  const [localReadIds, setLocalReadIds] = useState(new Set());
  const unreadNotifications = notificationsList.filter(
    (n) => !n.read_at && !localReadIds.has(n.id),
  );
  const unreadCount = unreadNotifications.length;
  // Mutation: Mark notification as read
  const markReadMutation = useMutation({
    mutationFn: (notificationId) =>
      markNotificationAsRead({ companyId, notificationId }),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: ['notifications', companyId],
      });
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        companyId,
      ]);

      queryClient.setQueryData(['notifications', companyId], (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: page.results.map((n) =>
              String(n.id) === String(notificationId)
                ? { ...n, read_at: new Date().toISOString() }
                : n,
            ),
          })),
        };
      });

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', companyId],
          context.previousNotifications,
        );
      }
    },
  });

  const handleMarkAsRead = (notificationId) => {
    setLocalReadIds((prev) => new Set(prev).add(notificationId));
    markReadMutation.mutate(notificationId);
  };

  useNotificationStream(companyId, (notification) => {
    if (notification?.id && !notification.read_at) {
      handleMarkAsRead(notification.id);
    }
    handleNotificationNavigation(notification);
  });

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const refreshToken = localStorage.getItem('refresh_token');
    try {
      if (refreshToken) {
        await logoutUser(refreshToken);
      }
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      try {
        await Promise.race([
          oneSignalLogout(),
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);
      } catch (err) {
        console.error('OneSignal logout error:', err);
      }
      queryClient.clear();
      clearConversationDrafts();
      localStorage.clear();
      toast.success('Logged out successfully');
      navigate(0);
    }
  };

  // Get user details from localStorage or API
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: getUserProfile,
    staleTime: 5 * 60 * 1000,
  });

  const [username, setUsername] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      let userObj = null;
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          userObj = JSON.parse(storedUser);
        }
      } catch (e) {
        console.error('Failed to parse user from localStorage', e);
      }

      const firstName = profile?.first_name ?? userObj?.first_name ?? '';
      const lastName = profile?.last_name ?? userObj?.last_name ?? '';
      const name =
        (firstName.trim() + ' ' + lastName.trim()).trim() ||
        profile?.username ||
        userObj?.username ||
        profile?.email ||
        userObj?.email ||
        'User';

      const compName =
        localStorage.getItem('company_name') ||
        profile?.company_name ||
        userObj?.company_name ||
        'Company';

      setUsername(name);
      setCompanyName(compName);
      setProfileImage(
        profile?.profile_image ||
          profile?.profile_picture ||
          userObj?.profile_image ||
          userObj?.profile_picture ||
          null,
      );
    };

    loadUser();

    window.addEventListener('user-profile-updated', loadUser);
    return () => {
      window.removeEventListener('user-profile-updated', loadUser);
    };
  }, [profile]);

  return (
    <header className="shrink-0 flex items-center justify-between w-full h-[64px] sm:h-[72px] laptop:!h-[62px] px-3 sm:px-6 laptop:!px-4 bg-white rounded-[16px] sm:rounded-[24px] shadow-sm gap-2">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <button
          type="button"
          id="dashboard-menu-button"
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={sidebarOpen}
          aria-controls="dashboard-sidebar"
          className={`p-2 text-main-black rounded-button lg:hidden shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
        >
          <svg
            className="w-7 h-7"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <h1
          id="page-heading"
          tabIndex={-1}
          className="text-lg sm:text-[22px] leading-6 sm:leading-[28px] font-bold font-sf-pro-display text-[#1F2241] truncate outline-none"
        >
          {pageTitle}
        </h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => {
              const newOpenState = !notificationOpen;
              setNotificationOpen(newOpenState);
              if (newOpenState) {
                refetchNotifications({
                  refetchPage: (page, index) => index === 0,
                });
              }
            }}
            type="button"
            aria-label="Open notifications"
            aria-expanded={notificationOpen}
            className="relative flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 laptop:!w-10 laptop:!h-10 rounded-[8px] bg-primary text-white hover:bg-primary-hover transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <img
              src={notificationIcon}
              alt=""
              className="w-6 h-6 object-contain"
            />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-white rounded-full" />
            )}
          </button>

          <NotificationDrawer
            isOpen={notificationOpen}
            onClose={() => setNotificationOpen(false)}
            companyId={companyId}
          />
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-label="Open account menu"
            aria-expanded={dropdownOpen}
            aria-haspopup="menu"
            className="flex items-center gap-3 p-1 rounded-button"
          >
            <img
              src={
                profileImage
                  ? profileImage.startsWith('http')
                    ? profileImage
                    : `${env.photoBaseUrl}${profileImage}`
                  : user_icon
              }
              alt="User Avatar"
              className="w-11 h-11 laptop:!w-10 laptop:!h-10 rounded-full object-cover border border-border-default pointer-events-none"
            />
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-[15px] leading-5 font-semibold text-[#1F2241] font-sf-pro-display whitespace-nowrap laptop:max-w-[160px] laptop:truncate">
                {companyName}
              </p>
              <p className="text-[13px] leading-4 font-normal text-text-secondary font-sf-pro-display whitespace-nowrap laptop:max-w-[160px] laptop:truncate">
                {username}
              </p>
            </div>
            <span className="hidden sm:flex text-primary shrink-0">
              {dropdownOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </span>
          </button>

          {dropdownOpen && (
            <div
              role="menu"
              data-overlay-container="true"
              style={{ overscrollBehavior: 'contain' }}
              className="absolute right-0 mt-2 w-[240px] max-w-[calc(100vw-16px)] bg-white border border-border-default rounded-2xl shadow-2xl overflow-hidden p-1.5 z-50 animate-fadeIn"
            >
              {canViewProfile && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    navigate('/profile');
                    setDropdownOpen(false);
                  }}
                  className="flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 rounded-xl text-sm font-medium text-text-primary hover:text-primary hover:bg-primary/10 transition-colors text-left whitespace-nowrap"
                >
                  <User className="w-[18px] h-[18px] text-primary shrink-0" />
                  <span className="whitespace-nowrap">My Profile</span>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  navigate('/settings');
                  setDropdownOpen(false);
                }}
                className="flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 rounded-xl text-sm font-medium text-text-primary hover:text-primary hover:bg-primary/10 transition-colors text-left whitespace-nowrap"
              >
                <Settings className="w-[18px] h-[18px] text-primary shrink-0" />
                <span className="whitespace-nowrap">Settings</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setDropdownOpen(false);
                  setShowLogoutConfirm(true);
                }}
                className="flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 rounded-xl text-sm font-medium text-text-primary hover:text-primary hover:bg-primary/10 transition-colors text-left whitespace-nowrap"
              >
                <LogOut className="w-[18px] h-[18px] text-primary shrink-0" />
                <span className="whitespace-nowrap">Log Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onCancel={() => {
          if (!isLoggingOut) setShowLogoutConfirm(false);
        }}
        onConfirm={handleLogout}
        isPending={isLoggingOut}
      />
    </header>
  );
}
